import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CASEBUILDER_BASE,
    DATA_MANAGER_HOME,
    DATA_MANAGER_WORKSPACE_LABEL,
    REGION_SETUP_ROUTE,
    getDataManagerStep,
} from '../../constants/setupRoadmapSteps';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SetupPageLayout from '../../components/setup/SetupPageLayout';
import ProtocolSelectionModal from '../../components/setup/ProtocolSelectionModal';
import ProtocolRow from '../../components/setup/ProtocolRow';
import {
    BlockEditorSheet,
    VIEW_MEMBERSHIP,
} from '../../components/blocks';
import api from '../../API';
import styles from './BlockSetup.module.css';

const blockStep = getDataManagerStep('block');

const CLOSED_SHEET = {
    open: false,
    mode: 'add',
    block: null,
    protocolId: null,
    initialView: VIEW_MEMBERSHIP,
};

function BlockSetup() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { local, isLoading } = useNetworkData();

    const [showAboutInfo, setShowAboutInfo] = useState(false);
    const [showProtocolModal, setShowProtocolModal] = useState(false);
    const [protocolEditId, setProtocolEditId] = useState(null);
    const [sheet, setSheet] = useState(CLOSED_SHEET);
    const [deleteError, setDeleteError] = useState(null);

    const { data: blocksResponse, refetch: refetchBlocks } = useQuery({
        queryKey: ['bdsa-block'],
        queryFn: () => api.get('/bdsa/block', { query: { includeRegions: true } }),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    const { data: regionsResponse } = useQuery({
        queryKey: ['bdsa-region'],
        queryFn: () => api.get('/bdsa/region/template'),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    const { data: protocolsResponse, refetch: refetchProtocols } = useQuery({
        queryKey: ['bdsa-protocol'],
        queryFn: () => api.get('/bdsa/protocol'),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    const blocks = blocksResponse?.status === 200 && Array.isArray(blocksResponse.data)
        ? blocksResponse.data
        : [];
    const regions = regionsResponse?.status === 200 && Array.isArray(regionsResponse.data)
        ? regionsResponse.data
        : [];
    const protocols = protocolsResponse?.status === 200 && Array.isArray(protocolsResponse.data)
        ? protocolsResponse.data
        : [];

    const hasRegions = regions.length > 0;
    const blocksByProtocol = useMemo(() => {
        const map = {};
        protocols.forEach((p) => {
            map[p._id] = blocks.filter((b) => String(b.protocolId) === String(p._id));
        });
        return map;
    }, [protocols, blocks]);

    const sheetProtocolId = sheet.protocolId || sheet.block?.protocolId || null;
    const sheetProtocolName = useMemo(() => {
        if (!sheetProtocolId) return null;
        const match = protocols.find((p) => String(p._id) === String(sheetProtocolId));
        return match?.name || null;
    }, [protocols, sheetProtocolId]);

    const scope = useMemo(() => (
        sheetProtocolId
            ? { type: 'protocol', protocolId: sheetProtocolId }
            : null
    ), [sheetProtocolId]);

    const handleProtocolCreated = useCallback(() => {
        setShowProtocolModal(false);
        refetchProtocols();
    }, [refetchProtocols]);

    const handleProtocolSelect = useCallback(() => {
        setShowProtocolModal(false);
        setProtocolEditId(null);
        refetchProtocols();
    }, [refetchProtocols]);

    const openCreateProtocol = useCallback(() => {
        setProtocolEditId(null);
        setShowProtocolModal(true);
    }, []);

    const openEditProtocol = useCallback((protocol) => {
        setProtocolEditId(protocol._id);
        setShowProtocolModal(true);
    }, []);

    const openAddBlock = useCallback((protocol) => {
        setSheet({
            open: true,
            mode: 'add',
            block: null,
            protocolId: protocol._id,
            initialView: VIEW_MEMBERSHIP,
        });
    }, []);

    const openEditBlock = useCallback((block) => {
        setSheet({
            open: true,
            mode: 'edit',
            block,
            protocolId: block.protocolId,
            initialView: VIEW_MEMBERSHIP,
        });
    }, []);

    const closeSheet = useCallback(() => {
        setSheet(CLOSED_SHEET);
        refetchBlocks();
    }, [refetchBlocks]);

    const handleDeleteProtocol = useCallback(async (protocol) => {
        setDeleteError(null);
        const response = await api.delete(`/bdsa/protocol/${protocol._id}`);
        if (response.status === 200) {
            queryClient.invalidateQueries(['bdsa-protocol']);
            queryClient.invalidateQueries(['bdsa-block']);
            refetchProtocols();
            refetchBlocks();
        } else {
            const msg = response.data?.message || response.data?.error || (typeof response.data === 'string' ? response.data : null) || `Failed to delete protocol (${response.status})`;
            setDeleteError(msg);
        }
    }, [queryClient, refetchProtocols, refetchBlocks]);

    if (!isLoading && !local) {
        return <div><h2>Server not configured</h2><p>Ask an administrator to complete initial server setup.</p></div>;
    }

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const hasBlocks = blocks.length > 0;
    const hasProtocols = protocols.length > 0;

    return (
        <SetupPageLayout
            stepNumber={blockStep.number}
            totalSteps={blockStep.total}
            title={blockStep.title}
            description={
                <>
                    <p className={styles.infoSummaryText}>
                        Create sectioning protocols and define blocks (and their regions) for each protocol. Cases use a protocol to get their block definitions.{' '}
                        <button
                            type="button"
                            className={styles.moreButton}
                            onClick={() => setShowAboutInfo((prev) => !prev)}
                            aria-expanded={showAboutInfo}
                        >
                            {showAboutInfo ? 'Less' : 'More'}
                        </button>
                    </p>
                    {showAboutInfo && (
                        <div className={styles.infoSection}>
                            <h4>About Protocols & Blocks</h4>
                            <p>A sectioning protocol is a named set of blocks used together (e.g., for a brain workup). Each block has a label and is mapped to one or more region definitions.</p>
                            <p>Create a protocol first, then add blocks to it. When you assign a protocol to a case, that case gets a copy of the protocol&apos;s blocks.</p>
                        </div>
                    )}
                </>
            }
            backLink={DATA_MANAGER_HOME}
            backLinkText={DATA_MANAGER_WORKSPACE_LABEL}
            onNext={hasBlocks ? () => navigate(CASEBUILDER_BASE) : undefined}
            onPrevious={() => navigate(REGION_SETUP_ROUTE)}
            nextDisabled={!hasBlocks}
            nextLabel={hasBlocks ? 'Next: Case Builder' : 'Create at least one block to continue'}
            previousLabel="Region Definitions"
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3>Sectioning Protocols & Blocks</h3>
                    <button
                        type="button"
                        onClick={openCreateProtocol}
                        className={styles.createButton}
                    >
                        + Create Sectioning Protocol
                    </button>
                </div>

                {!hasRegions && (
                    <div className={styles.regionDependency}>
                        <p>No region definitions found. Create at least one region definition before adding blocks.</p>
                        <button
                            type="button"
                            onClick={() => navigate(REGION_SETUP_ROUTE)}
                            className={styles.createButton}
                        >
                            Go to Region Definitions
                        </button>
                    </div>
                )}

                {hasRegions && !hasProtocols && (
                    <div className={styles.emptyState}>
                        <p>No sectioning protocols yet. Create one to define blocks for cases.</p>
                        <button
                            type="button"
                            onClick={openCreateProtocol}
                            className={styles.createButton}
                        >
                            Create Sectioning Protocol
                        </button>
                    </div>
                )}

                {hasRegions && hasProtocols && (
                    <div className={styles.protocolList}>
                        {deleteError && (
                            <div className={styles.errorMessage} role="alert">
                                {deleteError}
                            </div>
                        )}
                        {protocols.map((protocol) => (
                            <ProtocolRow
                                key={protocol._id}
                                protocol={protocol}
                                blocks={blocksByProtocol[protocol._id] || []}
                                hasRegions={hasRegions}
                                onEditProtocol={openEditProtocol}
                                onAddBlock={openAddBlock}
                                onEditBlock={openEditBlock}
                                onDeleteProtocol={handleDeleteProtocol}
                            />
                        ))}
                    </div>
                )}
            </div>

            <ProtocolSelectionModal
                isOpen={showProtocolModal}
                onClose={() => { setShowProtocolModal(false); setProtocolEditId(null); }}
                onSelect={protocolEditId ? handleProtocolSelect : handleProtocolCreated}
                editProtocolId={protocolEditId}
                createOnly={!protocolEditId}
            />

            {scope && (
                <BlockEditorSheet
                    open={sheet.open}
                    mode={sheet.mode}
                    block={sheet.block}
                    region={null}
                    initialView={sheet.initialView}
                    scope={scope}
                    protocolName={sheetProtocolName}
                    onClose={closeSheet}
                />
            )}
        </SetupPageLayout>
    );
}

export default BlockSetup;
